import { ReactElement, Ref, forwardRef, createContext, useCallback } from 'react';

import { useTheme, useMediaQuery, Dialog, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { useTimeout } from 'usehooks-ts';

import { ModalContext as ModalContextType, ModalRef, ModalProps } from './types';

import { useBoolean } from '../../common/hooks';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<unknown, ''> },
  ref: Ref<unknown>
) {
  return <Slide direction='down' ref={ref} {...props} />;
});

export const ModalContext = createContext<ModalContextType>({ fullScreen: false });

const Modal = forwardRef<ModalRef, ModalProps>(function Modal(props, ref): ReactElement | null {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const [isMounted, setIsMounted] = useBoolean();

  const { children, fullScreen = false, maxWidth = 'sm', open = false, onClose, ...rest } = props;

  const handleMountAnimation = useCallback(() => {
    setIsMounted.on();
  }, []);

  const handleUnmountAnimation = useCallback(() => {
    setIsMounted.off();
  }, []);

  useTimeout(handleMountAnimation, open ? 500 : null);

  useTimeout(handleUnmountAnimation, !open ? 500 : null);

  return isMounted ? (
    <ModalContext.Provider value={{ fullScreen: isXs || fullScreen, onClose }}>
      <Dialog
        {...rest}
        ref={ref}
        open={open}
        onClose={onClose ? () => onClose() : undefined}
        fullWidth
        fullScreen={isXs || fullScreen}
        maxWidth={maxWidth}
        PaperProps={{
          sx: {
            'width': '100%',
            'height': isXs || fullScreen ? '100vh' : 'auto',
            'minHeight': isXs || fullScreen ? '100vh' : 'none',

            'overflowY': 'hidden',

            'backgroundColor': 'background.default',
            'borderRadius': isXs || fullScreen ? 0 : 4,

            '&.MuiDialog-paper': {
              maxHeight: isXs || fullScreen ? '100vh' : 'calc(100% - 48px)',

              margin: isXs || fullScreen ? 0 : 3
            }
          }
        }}
        TransitionComponent={Transition}
        transitionDuration={{
          enter: theme.transitions?.duration?.complex || 750,
          exit: theme.transitions?.duration?.standard || 500
        }}>
        {children}
      </Dialog>
    </ModalContext.Provider>
  ) : null;
});

export default Modal;
